const debug = require('debug')('mtk:react:client');

import React, { Fragment } from 'react';
import classnames from 'classnames';
import { Link as RouterLink } from 'react-router-dom';
import { FiArrowUpRight, FiTrash2 } from 'react-icons/fi';

import api from '../utils/api';
import { useForm } from '../utils/form';
import { roles, roleLabels, modeLabels } from '../constants';

import { routes } from '../routes';
import { can } from '../can';

import { Loading, NotFound } from './PageStatus';

import PageHeader from '../components/common/PageHeader';
import Input, { InputRow } from '../components/common/Input';
import Toggle from '../components/common/Toggle';
import Select, { indexToOptions, objectToOptions } from '../components/common/Select';
import TextArea from '../components/common/TextArea';
import Button from '../components/common/Button';
import Link from '../components/common/Link';
import { List, Body, Row, Cell } from '../components/common/List';
import NotificationTemplates from '../components/NotificationTemplates';

import grid from '../styles/grid.css';
import css from './Client.css';

const clientFields = {
  name: ['text', true],
  segments: ['array'],
  preferences: {
    //whatsappFromNumber: ['text'],
    displayPickupCode: ['toggle'],
    printOrderParams: ['text'],
    twoFactorAuth: ['toggle'],
    loadPickupUsers: ['toggle'],
    loadPickupUsersRole: ['text'],
    enableCreateUserFromPickupInfo: ['toggle'],
    enableUsersNationalId: ['toggle'],
    medical: ['toggle'],
    enableProductsMenu: ['toggle'],
    searchByDropCode: ['toggle'],
    searchByPickupNationalId: ['toggle'],
    mode: ['text'],
    extras: ['json'],
    supportEmail: ['email'],
    supportPhone: ['text'],
    fromName: ['text'],
    fromEmail: ['email'],
    sendCourierAssignedEmail: ['toggle'],
    sendCourierAssignedSms: ['toggle'],
    sendPickupReservedEmail: ['toggle'],
    sendCancelPickupReservedEmail: ['toggle'],
    sendDropReadyEmail: ['toggle'],
    sendCourierCancelledEmail: ['toggle'],
    sendCourierCancelledSms: ['toggle'],
    sendCourierReadyEmail: ['toggle'],
    sendCourierReadySms: ['toggle'],
    sendPickupReadyEmail: ['toggle'],
    sendPickupReadySms: ['toggle'],
    sendPickupReadyWhatsApp: ['toggle'],
    sendPickupReminderEmail: ['toggle'],
    sendPickupReminderSms: ['toggle'],
    sendPickupReminderWhatsApp: ['toggle'],
    pickupReminderHours: [['number']],
    sendPickupReversalEmail: ['toggle'],
    sendPickupReversalSms: ['toggle'],
    sendCourierReversalEmail: ['toggle'],
    sendCourierReversalSms: ['toggle'],
    sendPickupCompletedEmail: ['toggle'],
    sendPickupCompletedSms: ['toggle'],
    sendPickupCompletedWhatsApp: ['toggle'],
    sendCourierCompletedEmail: ['toggle'],
    sendCourierCompletedSms: ['toggle'],
    webhookUrl: ['url'],
    webhookSecret: ['text'],
    autoReversalEnabled: ['toggle'],
    autoReversalHours: ['number'],
    autoCancelReturnEnabled: ['toggle'],
    autoCancelReturnHours: ['number'],
    autoCancelReservedEnabled: ['toggle'],
    autoCancelReservedHours: ['number'],
    autoDeleteReservedEnabled: ['toggle'],
    autoDeleteReservedHours: ['number'],
    customDropCodeEnabled: ['toggle'],
    customDropCodeRequired: ['toggle'],
    customPickupCodeEnabled: ['toggle'],
    courierPickupCodeEnabled: ['toggle'],
    courierPickupCodeRequired: ['toggle'],
    returnsEnabled: ['toggle'],
    sendReturnReservedEmail: ['toggle'],
    sendReturnReservedSms: ['toggle'],
    sendReturnReadyEmail: ['toggle'],
    sendReturnReadySms: ['toggle'],
    sendReturnCancelledEmail: ['toggle'],
    sendReturnCancelledSms: ['toggle'],
    sendReturnReversalEmail: ['toggle'],
    sendReturnReversalSms: ['toggle'],
    forceNumericPickupCode: ['toggle'],
    forcePickupCodeLength: ['number'],
    forceNumericDropCode: ['toggle'],
    forceDropCodeLength: ['number'],
    forceNumericDropReturnCode: ['toggle'],
    forceDropReturnCodeLength: ['number'],
    forceNumericPickupReturnCode: ['toggle'],
    forcePickupReturnCodeLength: ['number'],
    autoReversalSkipSundays: ['toggle'],
  },
};


const clientAdminTabs = [
  { id: 'config', label: 'Configuración' },
  { id: 'notifications', label: 'Notificaciones' },
  { id: 'preferences', label: 'Preferencias' },
  { id: 'flows', label: 'Flujos automáticos' },
  { id: 'options', label: 'Opciones, extras e integración' },
  { id: 'templates', label: 'Plantillas de notificación' },
];

const optionToggles = [
  ['loadPickupUsers', 'Carga de destinatarios desde usuarios'],
  ['enableCreateUserFromPickupInfo', 'Guardado de destinatarios como usuarios'],
  ['searchByDropCode', 'Búsqueda por DropCode'],
  ['searchByPickupNationalId', 'Búsqueda por ID nacional'],
  ['customDropCodeEnabled', 'Código de entrega personalizado'],
  ['customDropCodeRequired', 'Código de entrega personalizado exigido'],
  ['forceNumericDropCode', 'Código de entrega numérico'],
  ['forceNumericPickupCode', 'Código de retiro numérico'],
  ['customPickupCodeEnabled', 'Código de retiro personalizado'],
  ['customPickupCodeRequired', 'Código de retiro personalizado exigido'],
  ['displayPickupCode', 'Mostrar código de retiro'],
  ['courierPickupCodeEnabled', 'Código de retiro para revocación'],
  ['courierPickupCodeRequired', 'Código de retiro para revocación exigido'],
  ['returnsEnabled', 'Devoluciones'],
  ['forceNumericDropReturnCode', 'Código de devolución numérico'],
  ['forceNumericPickupReturnCode', 'Código de retiro de devolución numérico'],
  ['enableUsersNationalId', 'Campo RUT en usuarios'],
  ['medical', 'Ajustes médicos'],
  ['enableProductsMenu', 'Menú de productos'],
];

const notificationToggles = [
  ['sendPickupReservedEmail', 'Email de reserva a destinatario'],
  ['sendCancelPickupReservedEmail', 'Email de cancelación de reserva a destinatario'],
  ['sendPickupReadySms', 'SMS de orden lista para retiro'],
  ['sendPickupReadyEmail', 'Email de orden lista para retiro'],
  ['sendPickupReadyWhatsApp', 'WhatsApp de orden lista para retiro'],
  ['sendPickupReminderSms', 'SMS de recordatorio de retiro'],
  ['sendPickupReminderEmail', 'Email de recordatorio de retiro'],
  ['sendPickupReminderWhatsApp', 'WhatsApp de recordatorio de retiro'],
  ['sendPickupCompletedSms', 'SMS de envío completado'],
  ['sendPickupCompletedEmail', 'Email de envío completado'],
  ['sendPickupCompletedWhatsApp', 'WhatsApp de envío completado'],
  ['sendPickupReversalSms', 'SMS de revocación a destinatario'],
  ['sendPickupReversalEmail', 'Email de revocación a destinatario'],
  ['sendCourierAssignedSms', 'SMS de asignación a courier'],
  ['sendCourierAssignedEmail', 'Email de asignación a courier'],
  ['sendCourierCancelledSms', 'SMS de cancelación a courier'],
  ['sendCourierCancelledEmail', 'Email de cancelación a courier'],
  ['sendCourierReadySms', 'SMS de orden entregada a courier'],
  ['sendCourierReadyEmail', 'Email de orden entregada a courier'],
  ['sendCourierReversalSms', 'SMS de orden revocada a courier'],
  ['sendCourierReversalEmail', 'Email de orden revocada a courier'],
  ['sendCourierCompletedSms', 'SMS de orden completada a courier'],
  ['sendCourierCompletedEmail', 'Email de orden completada a courier'],
  ['sendReturnReservedSms', 'SMS de devolución reservada'],
  ['sendReturnReservedEmail', 'Email de devolución reservada'],
  ['sendReturnReadySms', 'SMS de devolución lista'],
  ['sendReturnReadyEmail', 'Email de devolución lista'],
  ['sendReturnCancelledSms', 'SMS de devolución cancelada'],
  ['sendReturnCancelledEmail', 'Email de devolución cancelada'],
  ['sendReturnReversalSms', 'SMS de devolución revocada'],
  ['sendReturnReversalEmail', 'Email de devolución revocada'],
];

const prefText = (form, key) => {
  const value = form.getValue(['preferences', key]);
  if (value == null || typeof value === 'object') return '';
  return String(value).trim();
};

const OverviewGroup = ({ title, items, empty }) => (
  <section className={css.overviewPanel}>
    <h2>{title}</h2>
    {items.length ? (
      <ul className={css.overviewList}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    ) : (
      <p className={css.overviewEmpty}>{empty}</p>
    )}
  </section>
);

const ClientOverview = ({ form }) => {
  const on = (key) => !!form.getValue(['preferences', key]);
  const text = (key) => prefText(form, key);
  const withHours = (label, hoursKey) => {
    const hours = text(hoursKey);
    return hours ? `${label}: ${hours} h` : label;
  };

  const options = [];
  if (text('mode')) options.push(`Modo: ${modeLabels.get(text('mode'))}`);
  optionToggles.forEach(([key, label]) => {
    if (!on(key)) return;
    if (key === 'loadPickupUsers' && text('loadPickupUsersRole')) {
      options.push(`${label}: ${roleLabels.get(text('loadPickupUsersRole'))}`);
      return;
    }
    options.push(label);
  });
  [
    ['forceDropCodeLength', 'Largo código de entrega'],
    ['forcePickupCodeLength', 'Largo código de retiro'],
    ['forceDropReturnCodeLength', 'Largo código de devolución'],
    ['forcePickupReturnCodeLength', 'Largo código de retiro de devolución'],
  ].forEach(([key, label]) => {
    if (text(key)) options.push(`${label}: ${text(key)}`);
  });
  if (text('printOrderParams')) options.push(`Etiquetas: ${text('printOrderParams')}`);
  const extras = form.getValue(['preferences', 'extras']);
  const extraKeys =
    extras && typeof extras.toJS === 'function'
      ? Object.keys(extras.toJS() || {})
      : extras && typeof extras === 'object'
        ? Object.keys(extras)
        : [];
  if (extraKeys.length) options.push(`Campos extras: ${extraKeys.join(', ')}`);
  if (text('webhookUrl')) options.push(`Webhook: ${text('webhookUrl')}`);

  const flows = [];
  if (on('autoReversalEnabled')) flows.push(withHours('Autorevocación de envíos', 'autoReversalHours'));
  if (on('autoReversalSkipSundays')) flows.push('Autorevocación omite domingos');
  if (on('autoCancelReturnEnabled')) flows.push(withHours('Autorevocación de devoluciones', 'autoCancelReturnHours'));
  if (on('autoCancelReservedEnabled')) flows.push(withHours('Auto cancelar reserva', 'autoCancelReservedHours'));
  if (on('autoDeleteReservedEnabled')) flows.push(withHours('Auto eliminar reserva', 'autoDeleteReservedHours'));

  const preferences = [
    `Nombre remitente: ${text('fromName') || 'Sin configurar'}`,
    `Email remitente: ${text('fromEmail') || 'Sin configurar'}`,
  ];
  if (text('supportEmail')) preferences.push(`Email de soporte: ${text('supportEmail')}`);
  if (text('supportPhone')) preferences.push(`Teléfono de soporte: ${text('supportPhone')}`);
  if (on('twoFactorAuth')) preferences.push('Doble factor requerido');

  const notifications = notificationToggles.filter(([key]) => on(key)).map(([, label]) => label);
  const reminderHours = form.getValue(['preferences', 'pickupReminderHours']);
  const reminderList = reminderHours && typeof reminderHours.toJS === 'function' ? reminderHours.toJS() : reminderHours;
  if (Array.isArray(reminderList) && reminderList.filter((item) => item != null && item !== '').length) {
    notifications.push(`Horas de recordatorio: ${reminderList.filter((item) => item != null && item !== '').join(', ')}`);
  }

  return (
    <div className={css.overview}>
      <OverviewGroup title="Opciones configuradas" items={options} empty="No hay opciones configuradas." />
      <OverviewGroup title="Flujos automáticos" items={flows} empty="No hay flujos automáticos activos." />
      <OverviewGroup title="Preferencias" items={preferences} empty="No hay preferencias configuradas." />
      <OverviewGroup title="Notificaciones activas" items={notifications} empty="No hay notificaciones activas." />
    </div>
  );
};

const Client = ({ match, history, location }) => {
  const { id } = match.params;
  const { user } = api.auth();

  const { item, actions } = api.clients.item(id, {}, { history });

  const { actions: segmentsActions } = api.segments.index(null, { lazy: true });

  const readOnly = !can(user, 'update', 'clients');
  const form = useForm(clientFields, item.data, {
    submit: (data) => {
      data = data.updateIn(['preferences'], (prefs) => item.data.get('preferences').merge(prefs));
      actions.update(data);
    },
  });

  const modeOptions = objectToOptions(modeLabels.obj);
  const roleOptions = objectToOptions(roleLabels.obj);

  const isAdmin = user.role == roles('admin');
  // ACTIONS
  const getSegments = async (search) => {
    const index = await segmentsActions.get({ search, scopeToClientId: id, perPage: 50 });
    return indexToOptions(index);
  };
  const onSegmentAdd = (name, value, option) => {
    actions.segmentAssign(value);
  };

  const onSegmentRemove = (name, value, option) => {
    actions.segmentUnassign(value);
  };

  const onDelete = async () => {
    await actions.delete();
  };

  React.useEffect(() => {
    if (!isAdmin) return;
    const hash = ((location && location.hash) || '').replace(/^#/, '');
    if (!clientAdminTabs.some((item) => item.id === hash)) history.replace(`${match.url}#config`);
  }, [isAdmin, location, history, match.url]);

  if (item.error && item.status == 404) return <NotFound />;
  if (!item.loaded) return <Loading />;

  const requestedTab = ((location && location.hash) || '').replace(/^#/, '');
  const tab = clientAdminTabs.some((item) => item.id === requestedTab) ? requestedTab : 'config';

  return (
    <main>
      <PageHeader
        readOnly={readOnly}
        section="clients"
        hideBreadcrumbs={!isAdmin}
        title={form.getValue('name')}
        dirty={form.dirty}
        autoSave
        {...item}>
        {can(user, 'delete', 'clients') && (
          <Button noMargin confirm="¿Eliminar este Cliente?" destructive onClick={onDelete}>
            Eliminar
          </Button>
        )}
      </PageHeader>

      {isAdmin && (
        <nav className={css.tabs}>
          {clientAdminTabs.map((item) => (
            <RouterLink
              key={item.id}
              to={`${match.url}#${item.id}`}
              className={classnames(css.tab, { [css.tabActive]: tab == item.id })}>
              {item.label}
            </RouterLink>
          ))}
        </nav>
      )}

      {!isAdmin ? (
        <ClientOverview form={form} />
      ) : (
      <div className={grid.grid}>
        {tab == 'config' && (
        <section className={grid.full}>
          <h2>Configuración</h2>
          <Input readOnly={readOnly} name="name" form={form} label="Nombre" placeholder="Maletek" />
          <Select
            readOnly={readOnly}
            name={['preferences', 'mode']}
            isClearable={false}
            options={modeOptions}
            label="Modo"
            form={form}
            note={'Define el modo en el que opera el cliente.'}
            placeholder="..."
          />

          <br />
          <hr />
          {isAdmin && (
            <Fragment>
              <h3>Grupos</h3>
              <Select
                isMulti
                readOnly={readOnly}
                isClearable={false}
                hideValues
                name={'segments'}
                value={indexToOptions(form.getValue('segments'))}
                onChange={() => { }}
                onSelectOption={onSegmentAdd}
                onDeselectOption={onSegmentRemove}
                loadOptions={getSegments}
              />
              <List>
                <Body>
                  {item.data &&
                    item.data.has('segments') &&
                    item.data
                      .get('segments')
                      .toJS()
                      .map((s, i) => (
                        <Row key={`segment-${i}`}>
                          <Cell>{s.name}</Cell>

                          {readOnly ? null : (
                            <Cell right inline>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  actions.segmentUnassign(s.id);
                                }}>
                                <FiTrash2 />
                              </a>

                              <Link inline to={routes('segment', { id: s.id })}>
                                <FiArrowUpRight />
                              </Link>
                            </Cell>
                          )}
                        </Row>
                      ))}
                </Body>
              </List>
            </Fragment>
          )}
        </section>
        )}

        {tab == 'options' && (
        <section className={grid.full}>
          <h2> Opciones de Orden </h2>
          <h3>Creación de orden</h3>
          <Toggle
            readOnly={readOnly}
            label="Habilitar carga de destinatario de orden desde lista de usuarios"
            name={['preferences', 'loadPickupUsers']}
            form={form}
          />
          {form.getValue(['preferences', 'loadPickupUsers']) && (
            <Select
              name={['preferences', 'loadPickupUsersRole']}
              isClearable={false}
              options={roleOptions}
              label="Rol de destinatario de orden"
              placeholder="..."
              form={form}
              readOnly={readOnly}
            />
          )}
          <Toggle
            readOnly={readOnly}
            label="Habilitar guardado de destinatarios como usuarios sin acceso"
            name={['preferences', 'enableCreateUserFromPickupInfo']}
            form={form}
          />

          <Toggle
            readOnly={readOnly}
            label="Permitir búsqueda por DropCode"
            name={['preferences', 'searchByDropCode']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Permitir búsqueda por ID Nacional"
            name={['preferences', 'searchByPickupNationalId']}
            form={form}
          />

          <hr />
          <h3>Código de entrega </h3>
          <small style={{ marginBottom: '1rem' }}>El código es autogenerado si no es entregado al crearlo</small>
          <Toggle
            readOnly={readOnly}
            label="Habilitar personalización de código de entrega"
            name={['preferences', 'customDropCodeEnabled']}
            form={form}
          />
          {form.getValue(['preferences', 'customDropCodeEnabled']) && (
            <Toggle
              readOnly={readOnly}
              label="Exigir código de entrega personalizado"
              name={['preferences', 'customDropCodeRequired']}
              form={form}
            />
          )}

          <Toggle
            readOnly={readOnly}
            label="Forzar código de entrega numérico"
            name={['preferences', 'forceNumericDropCode']}
            form={form}
          />
          <Input
            readOnly={readOnly}
            label="Largo del código de entrega"
            name={['preferences', 'forceDropCodeLength']}
            form={form}
            type="number"
            min={3}
            max={30}
          />


          <hr />
          <h3>Código de retiro </h3>
          <small style={{ marginBottom: '1rem' }}>El código es autogenerado si no es entregado al crearlo</small>
          <Toggle
            readOnly={readOnly}
            label="Habilitar personalización de código de retiro"
            name={['preferences', 'customPickupCodeEnabled']}
            form={form}
          />
          {form.getValue(['preferences', 'customPickupCodeEnabled']) && (
            <Toggle
              readOnly={readOnly}
              label="Exigir código de retiro personalizado"
              name={['preferences', 'customPickupCodeRequired']}
              form={form}
            />
          )}


          <Toggle
            readOnly={readOnly}
            label="Forzar código de retiro numérico"
            name={['preferences', 'forceNumericPickupCode']}
            form={form}
          />
          <Input
            readOnly={readOnly}
            label="Largo del código de retiro"
            name={['preferences', 'forcePickupCodeLength']}
            form={form}
            type="number"
            min={3}
            max={30}
          />



          <h3>Mostrar código de retiro</h3>
          <small style={{ marginBottom: '1rem' }}>Permite que los usuarios del cliente vean el códgo de retiro</small>
          <Toggle
            readOnly={readOnly}
            label="Mostrar código de retiro"
            name={['preferences', 'displayPickupCode']}
            form={form}
          />
          <br />
          <hr />
          <h3> Código de retiro (revocación) </h3>
          <Toggle
            readOnly={readOnly}
            label="Habilitar código de retiro para revocación"
            name={['preferences', 'courierPickupCodeEnabled']}
            form={form}
          />
          {form.getValue(['preferences', 'courierPickupCodeEnabled']) && (
            <Toggle
              readOnly={readOnly}
              label="Exigir código de retiro para revocación"
              name={['preferences', 'courierPickupCodeRequired']}
              form={form}
            />
          )}
          <br />
          <hr />

          <h3>Devoluciones</h3>
          <Toggle
            readOnly={readOnly}
            label="Habilitar devoluciones"
            name={['preferences', 'returnsEnabled']}
            form={form}
          />

          {form.getValue(['preferences', 'returnsEnabled']) && (
            <>
              <Toggle
                readOnly={readOnly}
                label="Forzar código de devolución numérico"
                name={['preferences', 'forceNumericDropReturnCode']}
                form={form}
              />
              <Input
                readOnly={readOnly}
                label="Largo del código de devolución"
                name={['preferences', 'forceDropReturnCodeLength']}
                form={form}
                type="number"
                min={3}
                max={30}
              />
              <Toggle
                readOnly={readOnly}
                label="Forzar código de retiro de devolución numérico"
                name={['preferences', 'forceNumericPickupReturnCode']}
                form={form}
              />
              <Input
                readOnly={readOnly}
                label="Largo del código de retiro de devolución"
                name={['preferences', 'forcePickupReturnCodeLength']}
                form={form}
                type="number"
                min={3}
                max={30}
              />
            </>
          )}

        </section>
        )}

        {tab == 'flows' && (
        <section className={grid.full}>
          <h2>Flujos automáticos</h2>
          <h3>Autorevocación</h3>

          <Toggle
            readOnly={readOnly}
            label="Activar autorevocación de envíos"
            name={['preferences', 'autoReversalEnabled']}
            form={form}
          />
          {form.getValue(['preferences', 'autoReversalEnabled']) && (
            <Input
              readOnly={readOnly}
              label="Horas hasta autorevocación de un envío"
              placeholder="72"
              step={1}
              min={1}
              max={168}
              name={['preferences', 'autoReversalHours']}
              form={form}
              note="Cantidad de horas desde que el paquete es entregado en puerta"
            />
          )}

          {form.getValue(['preferences', 'autoReversalEnabled']) && (
            <Toggle
              readOnly={readOnly}
              label="Omitir domingos en cálculo de autorevocación"
              name={['preferences', 'autoReversalSkipSundays']}
              form={form}
            />
          )}

          {form.getValue(['preferences', 'returnsEnabled']) && (
            <>
              <Toggle
                readOnly={readOnly}
                label="Activar autorevocación de devoluciones"
                name={['preferences', 'autoCancelReturnEnabled']}
                form={form}
              />

              {form.getValue(['preferences', 'autoCancelReturnEnabled']) && (
                <Input
                  readOnly={readOnly}
                  label="Horas hasta autorevocación de una devolución"
                  placeholder="72"
                  step={1}
                  min={1}
                  max={168}
                  name={['preferences', 'autoCancelReturnHours']}
                  form={form}
                  note="Cantidad de horas desde que la puerta es reservada"
                />
              )}
            </>
          )}
          <br />
          <hr />
          <h3>Auto cancelación o eliminación de reserva</h3>

          <Toggle
            readOnly={readOnly}
            label="Activar auto cancelar reserva"
            name={['preferences', 'autoCancelReservedEnabled']}
            form={form}
          />
          {form.getValue(['preferences', 'autoCancelReservedEnabled']) && (
            <Input
              readOnly={readOnly}
              label="Horas hasta auto cancelar reserva"
              placeholder="72"
              step={1}
              min={1}
              max={168}
              name={['preferences', 'autoCancelReservedHours']}
              form={form}
              note="Cantidad de horas desde que el paquete es reservado en el locker"
            />
          )}

          <Toggle
            readOnly={readOnly}
            label="Activar auto eliminar reserva"
            name={['preferences', 'autoDeleteReservedEnabled']}
            form={form}
          />

          {form.getValue(['preferences', 'autoDeleteReservedEnabled']) && (
            <Input
              readOnly={readOnly}
              label="Horas hasta auto eliminar reserva"
              placeholder="72"
              step={1}
              min={1}
              max={168}
              name={['preferences', 'autoDeleteReservedHours']}
              form={form}
              note="Cantidad de horas desde que el paquete es reservado en el locker"
            />
          )

          }

        </section>
        )}

        {tab == 'options' && (
        <section className={grid.full}>
          <h3>Opciones de etiquetas</h3>
          <Input
            readOnly={readOnly}
            label="Parámetros para imprimir etiquetas"
            name={['preferences', 'printOrderParams']}
            placeholder="pickupName, pickupPhone, extras.item"
            form={form}
            note={
              'Parámetros de la orden a imprimr bajo el codigo de retiro en la etiqueta, separados por coma. Ej: order.pickupName, order.pickupPhone, order.extras.item'
            }
          />

          <br />
          <hr />
          <h3>Opciones de usuarios</h3>
          <Toggle
            readOnly={readOnly}
            label="Activar campo RUT en usuarios"
            name={['preferences', 'enableUsersNationalId']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Activar ajustes médicos para usuarios"
            name={['preferences', 'medical']}
            form={form}
          />

          <br />
          <hr />
          <h3>Opciones de catálogo de productos</h3>
          <Toggle
            readOnly={readOnly}
            label="Activar menú de productos"
            name={['preferences', 'enableProductsMenu']}
            form={form}
          />


          <br />
          <hr />
          <h3>Campos Extras</h3>
          <TextArea readOnly={readOnly} type="json" name={['preferences', 'extras']} form={form} />
        </section>
        )}

        {tab == 'preferences' && (
        <section className={grid.full}>
          <h2>Preferencias </h2>

          <Input label="Email de Soporte" name={['preferences', 'supportEmail']} form={form} readOnly={readOnly} />
          <Input label="Teléfono de Soporte" name={['preferences', 'supportPhone']} form={form} readOnly={readOnly} />

          <Toggle
            readOnly={readOnly}
            label="Requerir autorización de doble factor"
            name={['preferences', 'twoFactorAuth']}
            form={form}
          />
          <small>Requerir 2FA para todos los usuarios del cliente.</small>
          <br />
          <br />
          <hr />

          {/*           <h2>Notificaciones</h2>
          <br />
          <Input
            label="Número de teléfono para WhatsApp"
            name={['preferences', 'whatsappFromNumber']}
            placeholder="+13257700024"
            form={form}
            readOnly={readOnly}
            note="El teléfono debe estar registrado y validado en Meta for whatsApp Bussines. Se usará el número de cliente eLocker por defecto."
          /> */}

          <h3>Remitente</h3>

          <Input
            label="Nombre remitente"
            name={['preferences', 'fromName']}
            placeholder="eLocker"
            form={form}
            readOnly={readOnly}
          />
          <Input
            label="Email remitente"
            name={['preferences', 'fromEmail']}
            placeholder="no-responder@email.elocker.cl"
            form={form}
            readOnly={readOnly}
            note="Email debe terminar en @elocker.cl o @elocker.com.pe, o haber sido validado con Maletek. Si no es válido los emails no se enviarán."
          />
        </section>
        )}

        {tab == 'notifications' && (
        <section className={grid.full}>
          <h2>Notificaciones</h2>
          <h3>Notificaciones destinatario </h3>

          <Toggle
            readOnly={readOnly}
            label="Envío de email de reserva a destinatario"
            name={['preferences', 'sendPickupReservedEmail']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email de cancelación de reserva a destinatario"
            name={['preferences', 'sendCancelPickupReservedEmail']}
            form={form}
          />
          <hr />

          <Toggle
            readOnly={readOnly}
            label="Envío de SMS de orden lista para retiro"
            name={['preferences', 'sendPickupReadySms']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email de orden lista para retiro"
            name={['preferences', 'sendPickupReadyEmail']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de whatsApp de orden lista para retiro"
            name={['preferences', 'sendPickupReadyWhatsApp']}
            form={form}
          />

          <hr />
          <Toggle
            readOnly={readOnly}
            label="Envío de SMS de recordatorio orden lista para retiro"
            name={['preferences', 'sendPickupReminderSms']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email de recordatorio orden lista para retiro"
            name={['preferences', 'sendPickupReminderEmail']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de whatsApp de recordatorio orden lista para retiro"
            name={['preferences', 'sendPickupReminderWhatsApp']}
            form={form}
          />

          {(form.getValue(['preferences', 'sendPickupReminderSms']) ||
            form.getValue(['preferences', 'sendPickupReminderEmail']) ||
            form.getValue(['preferences', 'sendPickupReminderWhatsApp'])) && (
              <>
                <InputRow>
                  {form.getValue(['preferences', 'pickupReminderHours']) &&
                    form.getValue(['preferences', 'pickupReminderHours']).map((item, index) => {
                      return (
                        <Input
                          key={`pickupReminderHours-${index}`}
                          label={`Recordatorio #${index + 1}`}
                          name={['preferences', 'pickupReminderHours', index]}
                          sufix="horas"
                          form={form}
                          readOnly={readOnly}
                        />
                      );
                    })}
                </InputRow>
                <Button onClick={() => form.appendValue(['preferences', 'pickupReminderHours'], null)}>+</Button>
                {form.getValue(['preferences', 'pickupReminderHours']).size > 1 && (
                  <Button
                    onClick={() =>
                      form.deleteValue([
                        'preferences',
                        'pickupReminderHours',
                        form.getValue(['preferences', 'pickupReminderHours']).size - 1,
                      ])
                    }>
                    -
                  </Button>
                )}
              </>
            )}
          <hr />
          <Toggle
            readOnly={readOnly}
            label="Envío de SMS envío completado"
            name={['preferences', 'sendPickupCompletedSms']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email envío completado"
            name={['preferences', 'sendPickupCompletedEmail']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de whatsApp envío completado"
            name={['preferences', 'sendPickupCompletedWhatsApp']}
            form={form}
          />
          <hr />
          <Toggle
            readOnly={readOnly}
            label="Envío de SMS de revocación"
            name={['preferences', 'sendPickupReversalSms']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email de revocación"
            name={['preferences', 'sendPickupReversalEmail']}
            form={form}
          />

          <hr />

          <h3>Notificaciones courier</h3>
          <Toggle
            readOnly={readOnly}
            label="Envío de SMS de asignación a courier"
            name={['preferences', 'sendCourierAssignedSms']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email de asignación a courier"
            name={['preferences', 'sendCourierAssignedEmail']}
            form={form}
          />

          <hr />
          <Toggle
            readOnly={readOnly}
            label="Envío de SMS de cancelación de orden"
            name={['preferences', 'sendCourierCancelledSms']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email de cancelación de orden"
            name={['preferences', 'sendCourierCancelledEmail']}
            form={form}
          />

          <hr />
          <Toggle
            readOnly={readOnly}
            label="Envío de SMS de orden entregada en locker"
            name={['preferences', 'sendCourierReadySms']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email de orden entregada en locker"
            name={['preferences', 'sendCourierReadyEmail']}
            form={form}
          />

          <hr />
          <Toggle
            readOnly={readOnly}
            label="Envío de SMS de orden revocada"
            name={['preferences', 'sendCourierReversalSms']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email de orden revocada"
            name={['preferences', 'sendCourierReversalEmail']}
            form={form}
          />

          <hr />
          <Toggle
            readOnly={readOnly}
            label="Envío de SMS de orden completada"
            name={['preferences', 'sendCourierCompletedSms']}
            form={form}
          />
          <Toggle
            readOnly={readOnly}
            label="Envío de email de orden completada"
            name={['preferences', 'sendCourierCompletedEmail']}
            form={form}
          />

          {form.getValue(['preferences', 'returnsEnabled']) && (
            <>
              <br />
              <hr />

              <h3>Notificaciones de devolución</h3>
              <Toggle
                readOnly={readOnly}
                label="Envío de SMS de devolución reservada"
                name={['preferences', 'sendReturnReservedSms']}
                form={form}
              />
              <hr />
              <Toggle
                readOnly={readOnly}
                label="Envío de email de devolución reservada"
                name={['preferences', 'sendReturnReservedEmail']}
                form={form}
              />
              <hr />

              <Toggle
                readOnly={readOnly}
                label="Envío de SMS de devolución lista"
                name={['preferences', 'sendReturnReadySms']}
                form={form}
              />
              <Toggle
                readOnly={readOnly}
                label="Envío de email de devolución lista"
                name={['preferences', 'sendReturnReadyEmail']}
                form={form}
              />
              <hr />

              <Toggle
                readOnly={readOnly}
                label="Envío de SMS de devolución cancelada"
                name={['preferences', 'sendReturnCancelledSms']}
                form={form}
              />
              <Toggle
                readOnly={readOnly}
                label="Envío de email de devolución cancelada"
                name={['preferences', 'sendReturnCancelledEmail']}
                form={form}
              />
              <hr />
              <Toggle
                readOnly={readOnly}
                label="Envío de SMS de devolución revocada"
                name={['preferences', 'sendReturnReversalSms']}
                form={form}
              />
              <Toggle
                readOnly={readOnly}
                label="Envío de email de devolución revocada"
                name={['preferences', 'sendReturnReversalEmail']}
                form={form}
              />
              
            </>
          )}
        </section>
        )}

        {tab == 'options' && (
        <section className={grid.full}>
          <h2> Integración </h2>
          <Input
            readOnly={readOnly}
            label="Webhook URL"
            name={['preferences', 'webhookUrl']}
            placeholder="https://servicio.com"
            form={form}
          />
          <Input
            readOnly={readOnly}
            label="Webhook Secret"
            name={['preferences', 'webhookSecret']}
            placeholder="token-secreto"
            form={form}
            note={'Será enviado en la llamada y puede ser utilizado para validación.'}
          />

          <br />
          <hr />

        </section>
        )}

        {tab == 'templates' && (
        <section className={grid.full}>
          <NotificationTemplates
            clientId={id}
            client={item.data}
            readOnly={readOnly}
            reloadClient={() => actions.get()}
          />
        </section>
        )}
      </div>
      )}
    </main>
  );
};

export default Client;
